import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyLifecycle,
  createOverride,
  getPolicy,
  listEvaluations,
  listOverrides,
  listPolicies,
  policyKeys,
  revokeOverride,
  simulatePolicy,
  updatePolicyDocument,
} from './policies-api';
import type {
  PolicyDocument,
  PolicyLifecycleAction,
  PolicyOverrideFormValues,
} from './types';

export function useBusinessPolicies() {
  return useQuery({
    queryKey: policyKeys.list(),
    queryFn: listPolicies,
    staleTime: 30_000,
  });
}

export function useBusinessPolicy(policyId: string | undefined) {
  return useQuery({
    queryKey: policyKeys.detail(policyId ?? '__none__'),
    queryFn: () => {
      if (!policyId) throw new Error('policyId is required');
      return getPolicy(policyId);
    },
    enabled: Boolean(policyId),
    staleTime: 30_000,
  });
}

export function usePolicyEvaluations(policyId: string | undefined) {
  return useQuery({
    queryKey: policyKeys.evaluations(policyId ?? '__none__'),
    queryFn: () => {
      if (!policyId) throw new Error('policyId is required');
      return listEvaluations(policyId);
    },
    enabled: Boolean(policyId),
    staleTime: 15_000,
  });
}

export function usePolicyOverrides(policyId: string | undefined) {
  return useQuery({
    queryKey: policyKeys.overrides(policyId ?? '__none__'),
    queryFn: () => {
      if (!policyId) throw new Error('policyId is required');
      return listOverrides(policyId);
    },
    enabled: Boolean(policyId),
    staleTime: 30_000,
  });
}

export function useUpdatePolicyDocument(policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (document: PolicyDocument) =>
      updatePolicyDocument(policyId, document),
    onSuccess: (next) => {
      qc.setQueryData(policyKeys.detail(policyId), next);
      qc.invalidateQueries({ queryKey: policyKeys.list() });
    },
  });
}

export function useApplyPolicyLifecycle(policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      action,
      reason,
    }: {
      action: PolicyLifecycleAction;
      reason: string;
    }) => applyLifecycle(policyId, action, reason),
    onSuccess: (next) => {
      qc.setQueryData(policyKeys.detail(policyId), next);
      qc.invalidateQueries({ queryKey: policyKeys.list() });
    },
  });
}

export function useCreatePolicyOverride(policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: PolicyOverrideFormValues) =>
      createOverride(policyId, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: policyKeys.overrides(policyId) });
    },
  });
}

export function useRevokePolicyOverride(policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (overrideId: string) => revokeOverride(policyId, overrideId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: policyKeys.overrides(policyId) });
    },
  });
}

/**
 * The simulator runs once per click, so a plain mutation hook is the right
 * surface (instead of a query). Result is returned directly to the caller.
 */
export function useSimulatePolicy(policyId: string) {
  return useMutation({
    mutationFn: (contextJson: string) => simulatePolicy(policyId, contextJson),
  });
}
